import { EActivityType } from '@constants';
import { ICreateActivity, IFile, IUpdateActivity } from '@sdk/contracts';
import { Button, Drawer, Form, Input, Space } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResourceWrapper } from './IntroductionForm.styles';

import { PdfPreview } from '@/components/ActivityPreview/PdfPreview';
import { Upload } from '@/components/Upload';
import { VideoPlayer } from '@/components/VideoPlayer';
import { RULES } from '@/constants';
import {
  useCourseDetail,
  useCurrentSection,
  useIntroductionForm,
  useMutateActivity
} from '@/store';

const DRAWER_WIDTH = 500;

export const IntroductionForm = () => {
  const { t } = useTranslation('CourseTranslations');
  const { isOpen, setIsOpen, activity, setActivity } = useIntroductionForm();
  const { createActivity, updateActivity, isWorking } = useMutateActivity();
  const [isHovered, setIsHovered] = useState(false);
  const { currentSectionId } = useCurrentSection();
  const { course } = useCourseDetail();
  const [form] = Form.useForm();

  const DRAWER_TITLE = useMemo(() => t(activity ? 'EDIT_ACTIVITY' : 'NEW_ACTIVITY'), [activity]);

  const file: IFile = Form.useWatch(['resource', 'file'], form);

  useEffect(() => {
    if (activity) {
      form.setFieldsValue(activity);
    }
  }, [activity, currentSectionId, course?.name]);

  if (!currentSectionId || !course) return null;

  const handleSubmit = (values: ICreateActivity['activity'] | IUpdateActivity['activity']) => {
    const newActivity = {
      ...values,
      type: file.mimetype.includes('video') ? EActivityType.VIDEO : EActivityType.PDF,
      resource: {
        file
      }
    };
    const onSuccess = handleCancel;

    if (values?._id) {
      return updateActivity(
        {
          courseName: course.name,
          activity: newActivity as IUpdateActivity['activity'],
          courseId: course._id,
          sectionId: currentSectionId
        },
        { onSuccess }
      );
    }

    createActivity(
      {
        courseName: course.name,
        sectionId: currentSectionId,
        activity: newActivity as ICreateActivity['activity'],
        courseId: course._id
      },
      { onSuccess }
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
    form.resetFields();
    setActivity();
  };

  return (
    <Drawer
      onClose={handleCancel}
      open={isOpen}
      destroyOnHidden
      width={DRAWER_WIDTH}
      title={DRAWER_TITLE}
      extra={
        <Space>
          <Button onClick={handleCancel}>{t('CANCEL')}</Button>
          <Button onClick={form.submit} type="primary" loading={isWorking}>
            {t('SAVE')}
          </Button>
        </Space>
      }>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item name="_id" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="name" rules={[RULES.REQUIRED]}>
          <Input placeholder={t('ACTIVITY_NAME')} />
        </Form.Item>
        <Form.Item name="description" rules={[RULES.REQUIRED]}>
          <Input.TextArea placeholder={t('ACTIVITY_DESCRIPTION')} rows={5} />
        </Form.Item>
        <Form.Item name={['resource', 'file']} rules={[RULES.REQUIRED]}>
          {file ? (
            <ResourceWrapper>
              {file.mimetype.includes('video') ? (
                <VideoPlayer file={file} />
              ) : (
                <PdfPreview file={file} />
              )}
              <Button
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                danger
                block
                onClick={() => form.setFieldsValue({ resource: null })}>
                {t(isHovered ? 'ACCEPTED_FILE_FORMATS' : 'DELETE_FILE')}
              </Button>
            </ResourceWrapper>
          ) : (
            <Upload
              block
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              label={t(isHovered ? 'ACCEPTED_FILE_FORMATS' : 'ADD_MATERIAL')}
              icon="Add"
              mode="upload"
              accept="application/pdf, video/*"
              maxCount={1}
              showUploadList={false}
            />
          )}
        </Form.Item>
      </Form>
    </Drawer>
  );
};
